import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "appSource" }
const _hoisted_2 = { class: "button-row source" }
const _hoisted_3 = {
  key: 0,
  class: "spacer source"
}
const _hoisted_4 = { class: "button-row" }
const _hoisted_5 = {
  key: 1,
  class: "spacer source"
}
const _hoisted_6 = { class: "button-row" }
const _hoisted_7 = {
  key: 2,
  class: "spacer source"
}
const _hoisted_8 = { class: "spacer source" }
const _hoisted_9 = { class: "spacer source" }

import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import jsyaml from 'js-yaml';

import Application from '../../models/applications';
import LabeledInput from '@components/Form/LabeledInput/LabeledInput.vue';
import LabeledSelect from '@shell/components/form/LabeledSelect.vue';
import FileSelector from '@shell/components/form/FileSelector.vue';
import GitPicker from '@shell/components/form/GitPicker.vue';
import RadioGroup from '@components/Form/Radio/RadioGroup.vue';
import { sortBy } from '@shell/utils/sort';
import { generateZip } from '@shell/utils/download';
import Collapse from '@shell/components/Collapse.vue';
import {
  APPLICATION_SOURCE_TYPE, 
  EpinioApplicationChartResource, 
  EpinioInfo, 
  //AppSourceArchive, 
  //AppSourceContainer, 
  //AppSourceGit, 
  //AppSourceGitUrl, 
  //AppSourceBuilderImage, 
  EpinioAppSource, 
  EPINIO_APP_MANIFEST
} from '../../types';
import { EpinioAppInfo } from '../../types';
import { _EDIT } from '@shell/config/query-params';
import { AppUtils } from '../../utils/application';

interface FileWithRelativePath extends File {
  // For some reason TS throws this as missing at transpile time .. so recreate it
   readonly webkitRelativePath: string;
}

// Todo: Ensure this uses the same default as the backend.
const DEFAULT_BUILD_PACK = 'paketobuildpacks/builder-jammy-full:latest';


export default /*@__PURE__*/_defineComponent({
  __name: 'AppSource',
  props: {
    application: {},
    source: {},
    info: {},
    mode: {}
  },
  emits: ["change", "changeAppInfo", "changeAppConfig", "valid"],
  setup(__props: any, { emit: __emit }) {


const GIT_BASE_URL = {
  [APPLICATION_SOURCE_TYPE.GIT_HUB]: 'https://github.com',
  [APPLICATION_SOURCE_TYPE.GIT_LAB]: 'https://gitlab.com',
};

/*interface Data {
  open: boolean,
  archive: AppSourceArchive,
  container: AppSourceContainer,
  git: AppSourceGit,
  gitUrl: AppSourceGitUrl,
  builderImage: AppSourceBuilderImage,
  types: any[],
  type: APPLICATION_SOURCE_TYPE ;// || { } from the select component
  APPLICATION_SOURCE_TYPE: typeof APPLICATION_SOURCE_TYPE
}*/

const store = useStore();

const t = store.getters['i18n/t'];

const props = __props;

const emit = __emit;

// Defaults
const defaultBuilderImage = ref(props.info?.default_builder_image || DEFAULT_BUILD_PACK);
const builderImageValue = ref(props.source?.builderImage?.value || defaultBuilderImage.value);

// Reactive State
const open = ref(false);
const gitSkipTypeReset = ref(false);
const archive = reactive({
  tarball: props.source?.archive?.tarball || '',
  fileName: props.source?.archive?.fileName || '',
});

const container = reactive({
  url: props.source?.container?.url || ''
});

const gitUrl = reactive({
  url: props.source?.gitUrl?.url || '',
  branch: props.source?.gitUrl?.branch || '',
  validGitUrl: false,
});

const git = reactive({
  usernameOrOrg: props.source?.git?.usernameOrOrg || '',
  repo: props.source?.git?.repo || '',
  commit: props.source?.git?.commit || '',
  branch: props.source?.git?.branch || '',
  url: props.source?.git?.url || '',
  sourceData: props.source?.git?.sourceData || {
    repos: [],
    branches: [],
    commits: []
  }
});

const builderImage = reactive({
  value: builderImageValue.value,
  default: builderImageValue.value === defaultBuilderImage.value
});

const EDIT = _EDIT;

const appChart = ref(props.source?.appChart);
const type = ref(props.source?.type || APPLICATION_SOURCE_TYPE.FOLDER);

// Derived and Computed
const types = Object.values(APPLICATION_SOURCE_TYPE).map(value => ({
  label: t(`epinio.applications.steps.source.${ value }.label`),
  value
}));

const namespaces = computed(() => sortBy(store.getters['epinio/all']('namespace'), 'name', false));
const appCharts = computed(() =>
  sortBy(store.getters['epinio/all']('appCharts'), 'name', false).map((ap: EpinioApplicationChartResource) => ({
    value: ap.meta.name,
    label: `${ap.meta.name} (${ap.short_description})`
  }))
);

const showBuilderImage = computed(() =>
  [APPLICATION_SOURCE_TYPE.ARCHIVE, APPLICATION_SOURCE_TYPE.FOLDER, APPLICATION_SOURCE_TYPE.GIT_URL, APPLICATION_SOURCE_TYPE.GIT_HUB, APPLICATION_SOURCE_TYPE.GIT_LAB].includes(type.value)
);

const gitSource = computed(() => ({
  type: type.value,
  selectedAccOrOrg: git.usernameOrOrg,
  selectedRepo: git.repo,
  selectedBranch: git.branch,
  selectedCommit: { sha: git.commit }
}));

const valid = ref(validate());

watch(type, () => {
  if (gitSkipTypeReset.value) {
    gitSkipTypeReset.value = false;
  } else {
    Object.assign(git, {});
  }
  update();
});

watch(valid, (val) => {
  emit('valid', val);
});

function validate() {
  switch (type.value) {
    case APPLICATION_SOURCE_TYPE.ARCHIVE:
    case APPLICATION_SOURCE_TYPE.FOLDER:
      return !!archive.tarball && !!builderImage.value;
    case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
      return !!container.url;
    case APPLICATION_SOURCE_TYPE.GIT_URL:
      return !!gitUrl.url && !!gitUrl.branch && !!builderImage.value && !!gitUrl.validGitUrl;
    case APPLICATION_SOURCE_TYPE.GIT_HUB:
    case APPLICATION_SOURCE_TYPE.GIT_LAB:
      return !!git.usernameOrOrg && !!git.url && !!git.repo && !!git.branch && !!git.commit;
  }
}

function update() {
  emit('change', {
    type: type.value,
    archive,
    container,
    gitUrl,
    builderImage,
    appChart: appChart.value,
    git
  });
  valid.value = validate();
}

function updateAppInfo(info: EpinioAppInfo) {
  emit('changeAppInfo', info);
}

function updateConfigurations(configs: string[]) {
  emit('changeAppConfig', configs);
}

function onImageType(defaultImage: boolean) {
  if (defaultImage) {
    builderImage.value = defaultBuilderImage.value;
  }
  builderImage.default = defaultImage;
  update();
}

function gitUpdate({ repo, selectedAccOrOrg, branch, commit, sourceData }: any) {
  if (!!selectedAccOrOrg && !!repo && !!commit && !!branch) {
    git.usernameOrOrg = selectedAccOrOrg;
    git.url = `${GIT_BASE_URL[type.value]}/${selectedAccOrOrg}/${repo.name}`;
    git.commit = commit;
    git.branch = branch;
    git.repo = repo;
    git.sourceData = sourceData;
    update();
    emit('valid', true);
  } else {
    update();
    emit('valid', false);
  }
}

function urlRule() {
  if (!gitUrl.url) return;

  const gitRegex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,})/gm;
  const result = gitRegex.exec(gitUrl.url);

  if (result && gitUrl.url === result[0]) {
    gitUrl.validGitUrl = true;
  } else {
    gitUrl.validGitUrl = false;
    return t('epinio.applications.steps.source.git_url.error.label');
  }
}

function onFileSelected(file: File) {
  archive.tarball = file;
  archive.fileName = file.name;
  update();
}

function onManifestFileSelected(file: string) {
  try {
    const parsed: any = jsyaml.load(file);
    const manifestType = AppUtils.getManifestSourceType(parsed.origin);
    gitSkipTypeReset.value = true;
    type.value = manifestType;

    switch (manifestType) {
      case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
        container.url = parsed.origin.container;
        break;
      case APPLICATION_SOURCE_TYPE.GIT_URL:
        gitUrl.url = parsed.origin.git.url;
        gitUrl.branch = parsed.origin.git.revision;
        break;
      case APPLICATION_SOURCE_TYPE.GIT_HUB:
      case APPLICATION_SOURCE_TYPE.GIT_LAB:
        Object.assign(git, AppUtils.getGitData(parsed.origin.git));
        break;
    }

    if (parsed.configuration) {
      appChart.value = parsed.configuration.appchart;
    }

    const appInfo: EpinioAppInfo = {
      meta: {
        name: parsed.name || '',
        namespace: namespaces.value?.[0]?.name || ''
      },
      configuration: {
        configurations: parsed.configuration?.configurations || [],
        instances: parsed.configuration.instances || 1,
        environment: parsed.configuration.environment || {},
        settings: parsed.configuration?.settings || {},
        routes: parsed.configuration.routes || []
      }
    };

    store.$router.replace({ query: { from: EPINIO_APP_MANIFEST } });
    update();
    updateAppInfo(appInfo);
    updateConfigurations(parsed.configuration.configurations || []);
  } catch (e) {
    console.error('Failed to parse manifest:', e);
  }
}

function onFolderSelected(files: FileWithRelativePath | FileWithRelativePath[]) {
  const safeFiles = Array.isArray(files) ? files : [files];
  let folderName = '';

  for (const f of safeFiles) {
    const paths = f.webkitRelativePath.split('/');
    if (paths.length > 1) {
      if (!folderName) {
        folderName = paths[0];
      } else if (folderName !== paths[0]) {
        folderName = '';
        break;
      }
    }
  }

  const filesToZip = safeFiles.reduce((res, f) => {
    let path = f.webkitRelativePath;
    if (folderName) {
      const parts = path.split('/');
      parts.shift();
      path = parts.join('/');
    }
    res[path] = f;
    return res;
  }, {} as { [key: string]: any });

  generateZip(filesToZip).then((zip: any) => {
    archive.tarball = zip;
    archive.fileName = folderName || 'folder';
    update();
  });
}

onMounted(() => {
  if (!appChart.value) {
    appChart.value = appCharts.value[0]?.value || appCharts.value[0];
  }
  update();
});


return (_ctx: any,_cache: any) => {
  const _directive_clean_tooltip = _resolveDirective("clean-tooltip")!
  const _directive_focus = _resolveDirective("focus")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(LabeledSelect, {
        value: type.value,
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((type).value = $event)),
        "data-testid": "epinio_app-source_type",
        label: "Source Type",
        options: _unref(types),
        mode: _ctx.mode,
        clearable: false
      }, null, 8, ["value", "options", "mode"]),
      _cache[11] || (_cache[11] = _createTextVNode()),
      _withDirectives(_createVNode(FileSelector, {
        "data-testid": "epinio_app-source_manifest",
        class: "role-tertiary add mt-5",
        label: _unref(t)('epinio.applications.steps.source.manifest.button'),
        mode: _ctx.mode,
        "raw-data": false,
        onSelected: onManifestFileSelected
      }, null, 8, ["label", "mode"]), [
        [_directive_clean_tooltip, _unref(t)('epinio.applications.steps.source.manifest.tooltip')]
      ])
    ]),
    _cache[22] || (_cache[22] = _createTextVNode()),
    (type.value === _unref(APPLICATION_SOURCE_TYPE).ARCHIVE)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.archive.file.label')), 1),
          _cache[13] || (_cache[13] = _createTextVNode()),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(LabeledInput, {
              value: archive.fileName,
              "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((archive.fileName) = $event)),
              "data-testid": "epinio_app-source_archive_name",
              disabled: true,
              tooltip: _unref(t)('epinio.applications.steps.source.archive.file.tooltip'),
              label: _unref(t)('epinio.applications.steps.source.archive.file.inputLabel'),
              required: true
            }, null, 8, ["value", "tooltip", "label"]),
            _cache[12] || (_cache[12] = _createTextVNode()),
            _createVNode(FileSelector, {
              "data-testid": "epinio_app-source_archive_file",
              class: "role-tertiary add mt-5",
              label: _unref(t)('epinio.applications.steps.source.archive.file.button'),
              mode: _ctx.mode,
              "raw-data": true,
              accept: '.zip, .tar, .gz, .bz2, .xz',
              onSelected: onFileSelected
            }, null, 8, ["label", "mode"])
          ])
        ]))
      : (type.value === _unref(APPLICATION_SOURCE_TYPE).FOLDER)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.folder.file.label')), 1),
            _cache[15] || (_cache[15] = _createTextVNode()),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(LabeledInput, {
                value: archive.fileName,
                "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((archive.fileName) = $event)),
                "data-testid": "epinio_app-source_folder_name",
                disabled: true,
                tooltip: _unref(t)('epinio.applications.steps.source.folder.file.tooltip'),
                label: _unref(t)('epinio.applications.steps.source.folder.file.inputLabel'),
                required: true
              }, null, 8, ["value", "tooltip", "label"]),
              _cache[14] || (_cache[14] = _createTextVNode()),
              _createVNode(FileSelector, {
                "data-testid": "epinio_app-source_folder_file",
                class: "role-tertiary add mt-5",
                label: _unref(t)('epinio.applications.steps.source.folder.file.button'),
                mode: _ctx.mode,
                "raw-data": true,
                directory: true,
                onSelected: onFolderSelected
              }, null, 8, ["label", "mode"])
            ])
          ]))
        : (type.value === _unref(APPLICATION_SOURCE_TYPE).CONTAINER_URL)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.container_url.url.label')), 1),
              _cache[16] || (_cache[16] = _createTextVNode()),
              _createVNode(LabeledInput, {
                value: container.url,
                "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((container.url) = $event)),
                "data-testid": "epinio_app-source_container",
                tooltip: _unref(t)('epinio.applications.steps.source.container_url.url.tooltip'),
                label: _unref(t)('epinio.applications.steps.source.container_url.url.inputLabel'),
                required: true,
                onInput: update
              }, null, 8, ["value", "tooltip", "label"])
            ]))
          : (type.value === _unref(APPLICATION_SOURCE_TYPE).GIT_URL)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.git_url.url.label')), 1),
                  _cache[17] || (_cache[17] = _createTextVNode()),
                  _withDirectives(_createVNode(LabeledInput, {
                    value: gitUrl.url,
                    "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((gitUrl.url) = $event)),
                    "data-testid": "epinio_app-source_git-url",
                    tooltip: _unref(t)('epinio.applications.steps.source.git_url.url.tooltip'),
                    label: _unref(t)('epinio.applications.steps.source.git_url.url.inputLabel'),
                    placeholder: 'https://github.com/{user or org}/{repository}',
                    required: true,
                    rules: [urlRule],
                    onDelay: ($event: any) => (100),
                    onInput: update
                  }, null, 8, ["value", "tooltip", "label", "rules"]), [
                    [_directive_focus]
                  ])
                ]),
                _cache[19] || (_cache[19] = _createTextVNode()),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.git_url.branch.label')), 1),
                  _cache[18] || (_cache[18] = _createTextVNode()),
                  _createVNode(LabeledInput, {
                    value: gitUrl.branch,
                    "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((gitUrl.branch) = $event)),
                    "data-testid": "epinio_app-source_git-branch",
                    tooltip: _unref(t)('epinio.applications.steps.source.git_url.branch.tooltip'),
                    label: _unref(t)('epinio.applications.steps.source.git_url.branch.inputLabel'),
                    required: true,
                    disabled: !gitUrl.validGitUrl,
                    onInput: update
                  }, null, 8, ["value", "tooltip", "label", "disabled"])
                ])
              ], 64))
            : (_openBlock(), _createBlock(GitPicker, {
                key: 4,
                value: gitSource.value,
                "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((gitSource).value = $event)),
                type: type.value,
                onChange: gitUpdate
              }, null, 8, ["value", "type"])),
    _cache[23] || (_cache[23] = _createTextVNode()),
    _createVNode(Collapse, {
      "value:open": open.value,
      "onUpdate:value:open": _cache[10] || (_cache[10] = ($event: any) => ((open).value = $event)),
      title: 'Advanced Settings',
      class: "pt-30 pb-30 source"
    }, {
      default: _withCtx(() => [
        _createVNode(LabeledSelect, {
          value: appChart.value,
          "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((appChart).value = $event)),
          "data-testid": "epinio_app-source_appchart",
          label: _unref(t)('epinio.applications.steps.source.archive.appchart.label'),
          options: appCharts.value,
          mode: _ctx.mode,
          clearable: false,
          required: true,
          tooltip: _unref(t)('typeDescription.appcharts'),
          reduce: (e) => e.value,
          disabled: _ctx.mode === _unref(EDIT),
          onInput: update
        }, null, 8, ["value", "label", "options", "mode", "tooltip", "reduce", "disabled"]),
        _cache[21] || (_cache[21] = _createTextVNode()),
        (showBuilderImage.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(RadioGroup, {
                value: builderImage.default,
                "onUpdate:value": _cache[8] || (_cache[8] = ($event: any) => ((builderImage.default) = $event)),
                class: "mt-20",
                name: "defaultBuilderImage",
                "data-testid": "epinio_app-source_builder-select",
                labels: [
            _unref(t)('epinio.applications.steps.source.archive.builderimage.default'),
            _unref(t)('epinio.applications.steps.source.archive.builderimage.custom')
          ],
                options: [true, false],
                "label-key": 'epinio.applications.steps.source.archive.builderimage.label',
                onInput: onImageType
              }, null, 8, ["value", "labels"]),
              _cache[20] || (_cache[20] = _createTextVNode()),
              _createVNode(LabeledInput, {
                value: builderImage.value,
                "onUpdate:value": _cache[9] || (_cache[9] = ($event: any) => ((builderImage.value) = $event)),
                "data-testid": "epinio_app-source_builder-value",
                disabled: builderImage.default,
                tooltip: _unref(t)('epinio.applications.steps.source.archive.builderimage.tooltip'),
                mode: _ctx.mode,
                onInput: update
              }, null, 8, ["value", "disabled", "tooltip", "mode"])
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _: 1,
      __: [21]
    }, 8, ["value:open"])
  ]))
}
}

})